import React, { useState, useEffect } from "react";
import Modal from "../Modal";
import api from "../../utils/api";
import { toast } from "react-toastify";
import Icon, { StarIcon, SendIcon } from '../common/icons';
import AssessmentIcon, { assessmentKey } from '../common/AssessmentIcon';

const FeedbackModal = ({ show, onClose, isLoggedIn, target, category }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [intendedCourse, setIntendedCourse] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false); // Track if user already submitted feedback

  const ratingLabels = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
  };

  useEffect(() => {
    if (show) {
      checkExistingFeedback(); // Check if user already submitted feedback when modal opens
    }
  }, [show]);

  const getQuestionText = () => {
    switch (category) {
      case "chatbot":
        return "Did ANNA recommend your intended course? If no, what course did you have in mind?";
      case "sdq":
      case "dass42":
      case "ryff":
        return "How was your experience?";
      default:
        return "How was your experience?";
    }
  };

  const getPlaceholder = () => {
    switch (category) {
      case "chatbot":
        return "Type your intended course";
      case "sdq":
      case "dass42":
      case "ryff":
        return "Share your thoughts about your experience";
      default:
        return "Share your thoughts about your experience";
    }
  };

  const checkExistingFeedback = async () => {
    try {
      const response = await api.get("/api/check-feedback/", {
        headers: isLoggedIn
          ? { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
          : {},
        params: { category }, // Send category to check for duplicates
      });

      if (response.status === 200 && response.data.exists) {
        setHasSubmitted(true);
        toast.info("You have already submitted feedback today.");
      } else {
        setHasSubmitted(false);
      }
    } catch (error) {
      console.error("Error checking feedback:", error);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e?.preventDefault?.();
    if (!rating) {
      toast.error("Please provide a rating.");
      return;
    }
    // Only require intended course for RIASEC (chatbot)
    if (category === "chatbot" && !intendedCourse.trim()) {
      toast.error("Please enter your intended course.");
      return;
    }

    if (hasSubmitted) {
      toast.info("You have already submitted feedback today.");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(
        "/api/submit-feedback/",
        {
          category, // 'chatbot' or 'landing_page'
          rating,
          feedback: feedbackComment.trim() || null, // Optional comment
          intended_course: intendedCourse.trim(), // Required on UI
        },
        {
          headers: isLoggedIn
            ? { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
            : {},
        },
      );

      if (response.status === 201) {
        toast.success("Thank you for your feedback!");
        setHasSubmitted(true);
        setRating(0);
        setHoverRating(0);
        setFeedbackComment("");
        setIntendedCourse("");
        setTimeout(onClose, 500); // Ensure UI updates before closing modal
      } else {
        toast.error(response.data?.error || "Failed to submit feedback.");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error(
        error.response?.data?.error || "Network error, please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    const titles = {
      chatbot: "Rate Your Experience", // RIASEC assessment 
      sdq: "Rate Your SDQ Assessment Experience", 
      dass42: "Rate Your DASS-42 Assessment Experience",
      ryff: "Rate Your Ryff Well-being Assessment Experience"
    };
    return titles[category] || "Rate Your Assessment Experience";
  };

  return (
    <Modal
      title={getModalTitle()}
      show={show}
      onHide={onClose}
      target={target}
      backdrop="static"
      keyboard={false}
    >
      {hasSubmitted ? (
        <div className="am-intro am-intro-only">
          <span className="am-intro-icon">
            <Icon name="check-circle" size={28} />
          </span>
          <h3 className="am-intro-title">Thank you for your feedback!</h3>
          <p className="am-intro-text">
            You have already submitted feedback today.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmitFeedback} className="am-form">
          <div className="am-intro">
            <span className="am-intro-icon">
              {assessmentKey(category) ? (
                <AssessmentIcon type={category} size={28} />
              ) : (
                <Icon name="chat" size={28} />
              )}
            </span>
            <h3 className="am-intro-title">
              How would you rate your experience with{" "}
              {(() => {
                const experiences = {
                  chatbot: "the Chatbot", // RIASEC assessment
                  sdq: "the SDQ Assessment",
                  dass42: "the DASS-42 Assessment",
                  ryff: "the Ryff Well-being Assessment"
                };
                return experiences[category] || "the Assessment";
              })()}?
            </h3>
          </div>

          <div className="am-rating">
            <div className="am-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                  aria-pressed={rating === star}
                  className={`am-star${(hoverRating || rating) >= star ? " is-on" : ""}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <StarIcon size={34} />
                </button>
              ))}
            </div>
            <span className={`am-stars-label${hoverRating || rating ? " is-set" : ""}`}>
              {(hoverRating && ratingLabels[hoverRating]) ||
                (rating ? ratingLabels[rating] : "Tap a star to rate")}
            </span>
          </div>

          {/* Only show required field for RIASEC (chatbot) */}
          {category === "chatbot" && (
            <label className="am-field">
              <span className="am-label">
                {getQuestionText()} <span className="am-required" aria-hidden="true">*</span>
              </span>
              <span className="am-input">
                <Icon name="graduation-cap" size={20} />
                <input
                  type="text"
                  placeholder={getPlaceholder()}
                  value={intendedCourse}
                  onChange={(e) => setIntendedCourse(e.target.value)}
                  required
                />
              </span>
            </label>
          )}

          <label className="am-field">
            <span className="am-label">
              Tell us more about your experience
            </span>
            <span className="am-input">
              <textarea
                rows="3"
                placeholder="What went well? What can we improve? (optional)"
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
              />
            </span>
          </label>

          <button
            type="submit"
            className="am-btn is-primary is-block is-large"
            disabled={
              loading ||
              hasSubmitted ||
              rating === 0 ||
              (category === "chatbot" && !intendedCourse.trim())
            }
          >
            {loading ? (
              <span
                className="spinner-border"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <SendIcon size={18} />
            )}
            {loading ? "Submitting..." : "Submit Feedback"}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default FeedbackModal;
